import { Box, DialogContent, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../../store/hooks";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { RootState } from "../../../../../store/store";
import {
  useCallback,
  useState,
  SetStateAction,
  Dispatch,
  useEffect,
  useMemo,
} from "react";
import MessageDialog from "../../../../../components/MessageDialog/MessageDialog";
import {
  IAsyncOptionFn,
  IDynamicFormFieldsJson,
} from "../../../../../models/user/DynamicForm/DynamicForm";
import {
  GetFOUsageAndOccupancyFormJson,
  UpdateFOUsageAndOccupancy,
} from "../../../../../store/user/policyChange/FOPolicyChangeActions";
import DynamicForm from "../../../../../common/DynamicForm/DynamicForm";
import { GetPolicyRisks, GetPolicyDetailsHeader } from "../../../../../store/user/policyDetails/policyDetailsActions";
import { IFOccupancyAndUsageUpdateRequestParams } from "../../../../../models/user/PolicyChange/FOPolicyChange";
import { setFOUsageAndOccupancyFormFieldsJson } from "../../../../../store/user/policyChange/FOPolicyChangeSlice";
import { getCookie, formatDateToYYYYMMDD } from "../../../../../utils/Utility";
import { setCalledReducerType } from "../../../../../store/common/CommonSlice";

interface IProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  changePolicyId?: string;
  changePolicyLobCode?: string;
  onClose?: () => void; // Close the main drawer
}

const UpdateFOUsageAndOccupancyComp: React.FC<IProps> = ({
  open,
  setOpen,
  changePolicyId,
  changePolicyLobCode,
  onClose,
}) => {
  const dispatch = useAppDispatch();

  let { policyId, lobCode } = useParams();
  policyId = policyId ? policyId : changePolicyId;
  lobCode = lobCode ? lobCode : changePolicyLobCode;

  const carrier = getCookie("carrier");
  const system = getCookie("system");

  const { userDetails } = useAppSelector((state: RootState) => state.userAuth);
  const { policyDetailsHeader } = useAppSelector((state: RootState) => state.policyDetails);

  useEffect(() => {
    if (policyId && carrier && system && lobCode) {
      dispatch(GetPolicyRisks({ policyId, carrier, system, lobCode }));
      dispatch(GetPolicyDetailsHeader({ policyId, carrier, system, lobCode }));
    }
  }, [policyId, carrier, system, lobCode, dispatch]);

  const { t } = useTranslation();

  const { foUsageAndOccupancyFormFieldsJson } = useAppSelector(
    (state: RootState) => state.foPolicyChange
  );

  const { isSuccess, isError, calledReducerType } = useAppSelector(
    (state: RootState) => state.common
  );

  const [formFieldsJson, setFormFieldsJson] = useState<IDynamicFormFieldsJson | null>(null);
  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);

  const asyncFunctions: { [key: string]: IAsyncOptionFn } = useMemo(() => ({}), []);

  const handleMessageDialogClose = useCallback(() => {
    setMessageDialogOpen(false);
    dispatch(setCalledReducerType(null));
    if (onClose) onClose();
  }, [dispatch, onClose]);

  const handleFormSubmit = (formValues: { [key: string]: any }) => {
    if (
      policyDetailsHeader &&
      policyDetailsHeader?.carrierPolicyNumber &&
      userDetails &&
      userDetails.customerId &&
      policyId &&
      system &&
      carrier &&
      lobCode
    ) {
      const changeDate = formValues.effectiveDate
        ? formatDateToYYYYMMDD(new Date(formValues.effectiveDate))
        : formatDateToYYYYMMDD(new Date());

      const requestParams: IFOccupancyAndUsageUpdateRequestParams = {
        carrierPolicyNo: policyDetailsHeader?.carrierPolicyNumber,
        changeDt: changeDate,
        customerId: userDetails?.customerId,
        policyRef: policyId,
        carrier,
        system,
        lobCode,
        detailDescription: "Updating FO usage & occupancy from UI",
        usageAndOccupancy: {
          numberofFamiliesLiving: formValues.numberofFamiliesLiving,
          yearRoofUpdated: formValues.yearRoofUpdated,
        },
      };

      dispatch(UpdateFOUsageAndOccupancy(requestParams));
    }
  };

  useEffect(() => {
    if ((isSuccess || isError) && calledReducerType === "foPolicyChange/UpdateFOUsageAndOccupancy") {
      setMessageDialogOpen(true);
      dispatch(setCalledReducerType(null));
    }
  }, [isSuccess, isError, calledReducerType, dispatch]);

  useEffect(() => {
    if (open) {
      dispatch(GetFOUsageAndOccupancyFormJson());
      dispatch(setCalledReducerType(null));
    }
  }, [dispatch, open]);

  useEffect(() => {
    if (foUsageAndOccupancyFormFieldsJson) {
      setFormFieldsJson(foUsageAndOccupancyFormFieldsJson);
    }
  }, [foUsageAndOccupancyFormFieldsJson]);

  useEffect(() => {
    return () => {
      dispatch(setFOUsageAndOccupancyFormFieldsJson(null));
    };
  }, [dispatch]);

  return (
    <>
      <MessageDialog handleClose={handleMessageDialogClose} open={messageDialogOpen} />
      <DialogContent id="update_fo_usage_occupancy_form_content" data-test-id="update_fo_usage_occupancy_form_content" aria-label="Update FO usage & occupancy form content">
        {formFieldsJson ? (
          <DynamicForm
            formFieldsJson={formFieldsJson}
            onFormSubmit={handleFormSubmit}
            asyncOptionsFunctions={asyncFunctions}
          />
        ) : (
          <Box sx={{ padding: "2rem", textAlign: "center" }}>
            <Typography>Loading form...</Typography>
          </Box>
        )}
      </DialogContent>
    </>
  );
};

export default UpdateFOUsageAndOccupancyComp; 